/** Response time tiers matching backend ResponseTimeTier enum */
export type ResponseTimeTier =
  | 'UNDER_10M'
  | 'UNDER_30M'
  | 'UNDER_1H'
  | 'UNDER_3H'
  | 'UNDER_6H'
  | 'UNDER_12H'
  | 'OVER_12H';

/** Raw API response from GET /profiles/{userId}/response-stats */
export interface ChatResponseStatsRaw {
  tier: ResponseTimeTier | null;
  /** Response rate as decimal (0-1), null if no data */
  responseRate: number | null;
  /** Average response time in ISO 8601 duration format (e.g., "PT30M"), null if no data */
  averageResponseTime: string | null;
  totalWindows: number;
  respondedWindows: number;
}

/** Transformed stats with computed fields for UI convenience */
export interface ChatResponseStats {
  tier: ResponseTimeTier | null;
  /** Response rate as percentage (0-100) for display */
  responseRatePercent: number | null;
  /** Average response time in seconds */
  averageResponseTimeSeconds: number | null;
  totalWindows: number;
  respondedWindows: number;
}

/**
 * Parses ISO 8601 duration string to total seconds.
 * Supports formats like "PT30M", "PT1H30M", "PT5H35M12S"
 */
export function parseIsoDuration(duration: string | null): number | null {
  if (duration === null) return null;

  // Match ISO 8601 duration format: PT[nH][nM][nS]
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/.exec(
    duration,
  );
  if (!match) return null;

  const hours = match[1] ? parseInt(match[1], 10) : 0;
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const seconds = match[3] ? parseFloat(match[3]) : 0;

  return hours * 3600 + minutes * 60 + seconds;
}

export function transformResponseStats(
  raw: ChatResponseStatsRaw,
): ChatResponseStats {
  return {
    tier: raw.tier,
    responseRatePercent:
      raw.responseRate !== null ? raw.responseRate * 100 : null,
    averageResponseTimeSeconds: parseIsoDuration(raw.averageResponseTime),
    totalWindows: raw.totalWindows,
    respondedWindows: raw.respondedWindows,
  };
}

export const RESPONSE_TIME_TIER_CONFIG: Record<
  ResponseTimeTier,
  { label: string; color: string; shortLabel: string }
> = {
  UNDER_10M: {
    label: 'Replies within ~10m',
    shortLabel: '10m',
    color: 'var(--Cardgreen-400, #7FDC33)',
  },
  UNDER_30M: {
    label: 'Replies within ~30m',
    shortLabel: '30m',
    color: 'var(--Cardgreen-400, #7FDC33)',
  },
  UNDER_1H: {
    label: 'Replies within ~1h',
    shortLabel: '1h',
    color: 'var(--Positive-600, #6FC158)',
  },
  UNDER_3H: {
    label: 'Replies within ~3h',
    shortLabel: '3h',
    color: 'var(--Positive-600, #6FC158)',
  },
  UNDER_6H: {
    label: 'Replies within ~6h',
    shortLabel: '6h',
    color: 'var(--Pending-500, #F29740)',
  },
  UNDER_12H: {
    label: 'Replies within ~12h',
    shortLabel: '12h',
    color: 'var(--Pending-500, #F29740)',
  },
  OVER_12H: {
    label: 'Replies within ~1d',
    shortLabel: '1d',
    color: 'var(--Danger-400, #E9817C)',
  },
};

export function formatResponseTime(seconds: number | null): string {
  if (seconds === null) return 'N/A';

  if (seconds < 60) return `${Math.round(seconds).toString()}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60).toString()}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600).toString()}h`;
  return `${Math.round(seconds / 86400).toString()}d`;
}
